/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { ButtonComponent } from '@app/app/shared/styleguide/components/buttons/default/button.component';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import {
  LucideDownload,
  LucideFile,
  LucideFileArchive,
  LucideFileAudio,
  LucideFileImage,
  LucideFileSpreadsheet,
  LucideFileText,
  LucideFileVideo,
  LucideIconInput,
  LucidePresentation,
} from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { IconComponent } from '../icon/icon.component';

const ICON_BY_EXTENSION: Record<string, LucideIconInput> = {
  pdf: LucideFileText,
  doc: LucideFileText,
  docx: LucideFileText,
  odt: LucideFileText,
  rtf: LucideFileText,
  txt: LucideFileText,
  xls: LucideFileSpreadsheet,
  xlsx: LucideFileSpreadsheet,
  ods: LucideFileSpreadsheet,
  csv: LucideFileSpreadsheet,
  ppt: LucidePresentation,
  pptx: LucidePresentation,
  odp: LucidePresentation,
  png: LucideFileImage,
  jpg: LucideFileImage,
  jpeg: LucideFileImage,
  gif: LucideFileImage,
  webp: LucideFileImage,
  svg: LucideFileImage,
  mp3: LucideFileAudio,
  wav: LucideFileAudio,
  ogg: LucideFileAudio,
  flac: LucideFileAudio,
  m4a: LucideFileAudio,
  mp4: LucideFileVideo,
  mov: LucideFileVideo,
  avi: LucideFileVideo,
  webm: LucideFileVideo,
  zip: LucideFileArchive,
  rar: LucideFileArchive,
  '7z': LucideFileArchive,
};

/**
 * Compact, wide card presenting an uploaded file as a download rather than an inline embed.
 * Used by the tiptap media nodes in `DOWNLOAD` display mode and by the published content preview.
 */
@Component({
  selector: 'ptl-file-download-card',
  standalone: true,
  imports: [TranslocoModule, IconComponent, ButtonComponent],
  templateUrl: './file-download-card.component.html',
  styleUrls: ['./file-download-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileDownloadCardComponent {
  readonly fileName = input<string | null | undefined>(null);
  readonly url = input.required<string>();
  readonly caption = input<string | null | undefined>(null);

  readonly LucideDownload = LucideDownload;
  readonly downloadLabelKey = marker('translations.tiptap.fileDownloadCard.download');
  readonly downloadAriaLabelKey = marker('translations.tiptap.fileDownloadCard.downloadAriaLabel');
  readonly fallbackNameKey = marker('translations.tiptap.fileDownloadCard.untitledFile');

  /** The extension of the file, lower-cased and without the dot, or null when it cannot be determined. */
  private readonly extension = computed<string | null>(() => extractExtension(this.fileName()) ?? extractExtension(this.url()));

  readonly displayName = computed<string | null>(() => (this.fileName()?.trim() || fileNameFromUrl(this.url()))?.split('/')?.at(-1));

  readonly typeLabel = computed<string | null>(() => this.extension()?.toUpperCase() ?? null);

  readonly icon = computed<LucideIconInput>(() => {
    const extension = this.extension();
    return (extension && ICON_BY_EXTENSION[extension]) || LucideFile;
  });

  download() {
    const url = this.url();
    if (url) {
      window.open(url, '_blank');
    }
  }
}

/** Strips the query string off a URL and returns the last path segment, or null when there is none. */
function fileNameFromUrl(url: string | null | undefined): string | null {
  const path = url?.split('?')[0]?.split('#')[0];
  const lastSegment = path?.split('/').pop();
  if (!lastSegment) {
    return null;
  }
  try {
    return decodeURIComponent(lastSegment);
  } catch {
    return lastSegment;
  }
}

function extractExtension(value: string | null | undefined): string | null {
  const name = value?.includes('/') ? fileNameFromUrl(value) : value?.split('?')[0];
  const extension = name?.includes('.') ? name.split('.').pop()?.toLowerCase() : null;
  return extension && /^[a-z0-9]{1,5}$/.test(extension) ? extension : null;
}
